import {FC, useState} from "react";
import {Button, Calendar, CalendarProps, Card, Flex, Radio, RadioGroupProps, Switch} from "antd";
import {useEventCalendar} from "../../queries/useEventCalendar.ts";
import dayjs, {Dayjs} from "dayjs";
import {EventCalendarItem} from "./components/EventCalendarItem/EventCalendarItem.tsx";
import styles from './CalendarPage.module.css';
import {useIsMobile} from "../../hooks/useIsMobile.ts";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {useOwnProfile} from "../../stores/OwnProfileStore.ts";
import {CalendarEvent} from "../../types/domain.ts";

type FilterParams = {
    rawData: CalendarEvent[] | undefined;
    value: string;
    showDraft: boolean;
    hideCancelled: boolean;
};

const filterCalendarEvents = ({ rawData, value, showDraft, hideCancelled }: FilterParams) => {
    if (!rawData) return undefined;

    return rawData.filter(({ calendar_type }) => {
        if (value !== 'all') {
            if (value === 'c/r') {
                if (calendar_type !== 'cancelled' && calendar_type !== 'returned') return false;
            } else if (calendar_type !== value) {
                return false;
            }
        }

        if (!showDraft && calendar_type === 'draft') return false;

        return !(hideCancelled && value !== 'c/r' && calendar_type === 'cancelled');
    });
};

export const CalendarPage: FC = () => {
    const isMobile = useIsMobile();
    const { profile } = useOwnProfile();

    const options: RadioGroupProps['options'] = [
        { label: 'Все', value: 'all' },
        { label: <>В&nbsp;{isMobile ? 'обраб.' : 'обработке'}</>, value: 'processing' },
        { label: <>Ожидает&nbsp;вашего&nbsp;{isMobile ? 'соглас.' : 'согласования'}</>, value: 'awaits_own_approval' },
        { label: isMobile ? 'Утвержд.' : 'Утвержденные', value: 'approved' },
        ...(profile?.role === 'staff' ? [{ label: <>Отмена&nbsp;/&nbsp;возврат</>, value: 'c/r' }] : [])
    ];

    const [value, setValue] = useState('all');
    const [showDraft, setShowDraft] = useState(false);
    const [hideCancelled, setHideCancelled] = useState(false);

    const { data: rawData } = useEventCalendar();
    const data = filterCalendarEvents({
        rawData,
        value,
        showDraft,
        hideCancelled
    });

    const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
        const items = data?.filter(({ date }) =>
            dayjs.utc(date).isSame(current.utc(true), 'date')
        );

        if(items?.length && info.type === 'date') {
            return <EventCalendarItem items={items} />;
        }

        return null;
    }

    const [selectedDate, setSelectedDate] = useState(dayjs());

    return (
        <Card loading={!data} className={styles.card}>
            <Flex gap={32} className={styles.filterContainer}>
                <Radio.Group
                    block
                    options={options}
                    value={value}
                    onChange={({ target }) => setValue(target.value)}
                    optionType="button"
                    buttonStyle="solid"
                />
                {profile?.role === 'staff' && (
                    <>
                        <Flex gap={8} align="center">
                            <Switch value={showDraft} onChange={(v) => setShowDraft(v)} />
                            Показывать черновики
                        </Flex>
                        <Flex gap={8} align="center">
                            <Switch value={hideCancelled} onChange={(v) => setHideCancelled(v)} />
                            Скрывать отменённые
                        </Flex>
                    </>
                )}
                <Flex gap={8}>
                    <Button icon={<LeftOutlined />} onClick={() => setSelectedDate((v) => v.subtract(1, 'month').startOf('month'))} />
                    <Button icon={<RightOutlined />} onClick={() => setSelectedDate((v) => v.add(1, 'month').startOf('month'))} />
                </Flex>
            </Flex>
            <div onWheel={(e) => setSelectedDate((v) => (e.deltaY < 0 ? v.subtract(1, 'month') : v.add(1, 'month')).startOf('month'))}>
                <Calendar
                    cellRender={cellRender}
                    value={selectedDate}
                    onChange={setSelectedDate}
                />
            </div>
        </Card>
    );
}